import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface Diffusion {
  id: string;
  bar_id: string;
  match_id: string;
  status: 'unconfirmed' | 'probable' | 'confirmed';
  confirmed_at: string | null;
  confirmed_by: string | null;
  bars: { id: string; name: string } | null;
  matches: {
    id: string;
    home_team: string;
    away_team: string;
    match_date: string;
    match_time: string;
    competition: string;
  } | null;
}

interface Filters {
  dateRange?: 'today' | 'tomorrow' | 'week' | 'all';
  status?: string;
  barId?: string;
}

export function useDiffusions(filters?: Filters) {
  const [diffusions, setDiffusions] = useState<Diffusion[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchDiffusions = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('diffusions')
        .select('id, bar_id, match_id, status, confirmed_at, confirmed_by, bars(id, name), matches(id, home_team, away_team, match_date, match_time, competition)')
        .limit(300);

      if (filters?.status) query = query.eq('status', filters.status);
      if (filters?.barId) query = query.eq('bar_id', filters.barId);

      const { data, error } = await query;
      if (error) throw error;

      let result = (data || []) as unknown as Diffusion[];

      // Client-side date filtering
      const today = new Date().toISOString().split('T')[0];
      if (filters?.dateRange === 'today') {
        result = result.filter(d => d.matches?.match_date === today);
      } else if (filters?.dateRange === 'tomorrow') {
        const tm = new Date();
        tm.setDate(tm.getDate() + 1);
        const tomorrow = tm.toISOString().split('T')[0];
        result = result.filter(d => d.matches?.match_date === tomorrow);
      } else if (filters?.dateRange === 'week') {
        const end = new Date();
        end.setDate(end.getDate() + 7);
        const endStr = end.toISOString().split('T')[0];
        result = result.filter(d => d.matches && d.matches.match_date >= today && d.matches.match_date <= endStr);
      }

      // Sort by match date/time
      result.sort((a, b) => {
        const dateA = (a.matches?.match_date || '') + (a.matches?.match_time || '');
        const dateB = (b.matches?.match_date || '') + (b.matches?.match_time || '');
        return dateA.localeCompare(dateB);
      });

      setDiffusions(result);
    } catch (err) {
      console.error('Fetch diffusions error:', err);
    } finally {
      setLoading(false);
    }
  }, [filters?.dateRange, filters?.status, filters?.barId]);

  useEffect(() => {
    fetchDiffusions();
  }, [fetchDiffusions]);

  const updateStatus = async (id: string, status: string) => {
    const { error } = await supabase
      .from('diffusions')
      .update({
        status,
        confirmed_at: new Date().toISOString(),
        confirmed_by: 'admin',
      })
      .eq('id', id);
    if (error) throw error;
    await fetchDiffusions();
  };

  const createDiffusion = async (barId: string, matchId: string, status: string = 'unconfirmed') => {
    const { error } = await supabase
      .from('diffusions')
      .insert({ bar_id: barId, match_id: matchId, status });
    if (error) throw error;
    await fetchDiffusions();
  };

  return { diffusions, loading, refetch: fetchDiffusions, updateStatus, createDiffusion };
}
